/**
 * TenantsPanel — Liste détaillée des locataires (avec accordéon contact admin).
 *
 * Extrait de AdminImmeublePortalMirror.jsx (Batch #14.221).
 */
import { useState, useMemo } from "react";
import { Users, Home, Mail, Phone, Wallet, ChevronDown, ChevronUp, Pencil, Plus, Smartphone } from "lucide-react";
import { PanelTitle, EmptyState } from "./_shared";
import { DetailRow, ManualEmailActions, fmtMoney } from "./UnitsPanel";

function TenantsPanel({ tenants, activeLeasesByUnit, unitsById, buildingsById, onEdit, onCreate, canCreate }) {
  const [expanded, setExpanded] = useState(null);

  // Batch #14.182 — Contexte = unit_id direct du tenant (pas via lease)
  const tenantContext = useMemo(() => {
    const ctx = {};
    for (const t of tenants) {
      if (!t.unit_id) continue;
      const unit = unitsById[t.unit_id];
      const lease = activeLeasesByUnit[t.unit_id];
      ctx[t.id] = {
        unit,
        lease,
        building: unit ? buildingsById[unit.property_id] : null,
      };
    }
    return ctx;
  }, [tenants, activeLeasesByUnit, unitsById, buildingsById]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2">
        <PanelTitle icon={Users} label="Locataires" count={tenants.length} tone="indigo" inline />
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            disabled={!canCreate}
            className="text-[11px] px-2.5 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1 font-semibold shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="mirror-tenant-create"
            title={canCreate ? "Ajouter un locataire au nom du client" : "Créer d'abord un logement"}
          >
            <Plus className="w-3 h-3" /> Ajouter un locataire
          </button>
        )}
      </div>
      {tenants.length === 0 ? (
        <EmptyState label="Aucun locataire enregistré." />
      ) : (
        <ul className="space-y-1.5">
          {tenants.map((t) => {
            const ctx = tenantContext[t.id] || {};
            const isOpen = expanded === t.id;
            const fullName = [t.first_name, t.last_name].filter(Boolean).join(" ") || "Locataire";
            const isInactive = t.is_active === false;
            return (
              <li key={t.id}
                  className={`rounded-lg border ${isInactive ? "border-slate-200 bg-slate-50/60" : "border-blue-100 bg-blue-50/40"}`}
                  data-testid={`mirror-tenant-${t.id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : t.id)}
                  className="w-full flex items-start gap-3 p-2.5 text-left hover:bg-white/50 transition rounded-lg"
                  data-testid={`mirror-tenant-toggle-${t.id}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isInactive ? "bg-slate-200 text-slate-500" : "bg-blue-100 text-blue-700"}`}>
                    <Users className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-800 flex items-center gap-1.5 flex-wrap">
                      {fullName}
                      {isInactive && <span className="text-[9px] uppercase bg-slate-200 text-slate-600 px-1 py-0.5 rounded">inactif</span>}
                      {/* Batch #14.189 — Badge SMS opt-in */}
                      {t.notify_by_sms ? (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-200"
                          data-testid={`mirror-tenant-sms-badge-on-${t.id}`}
                          title="Ce locataire a activé les notifications SMS"
                        >
                          <Smartphone className="w-2.5 h-2.5" /> SMS ON
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200"
                          data-testid={`mirror-tenant-sms-badge-off-${t.id}`}
                          title={t.phone ? "Notifications SMS désactivées pour ce locataire" : "Pas de téléphone — SMS impossible"}
                        >
                          <Smartphone className="w-2.5 h-2.5" /> SMS OFF
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      {t.phone && (
                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {t.phone}</span>
                      )}
                      {t.email && (
                        <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {t.email}</span>
                      )}
                      {ctx.unit && (
                        <span className="inline-flex items-center gap-1">
                          <Home className="w-3 h-3" /> Apt {ctx.unit.unit_label}
                          {ctx.building?.name && ` — ${ctx.building.name}`}
                        </span>
                      )}
                    </div>
                  </div>
                  {t.balance_due !== 0 && t.balance_due != null && (
                    <span className={`tabular-nums font-semibold text-xs flex items-center gap-1 mr-2 ${t.balance_due > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      <Wallet className="w-3 h-3" />
                      {fmtMoney(t.balance_due)}
                    </span>
                  )}
                  {isOpen ? <ChevronUp className="w-3 h-3 text-slate-400 mt-1.5" /> : <ChevronDown className="w-3 h-3 text-slate-400 mt-1.5" />}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-200 px-3 py-2.5 bg-white/50 space-y-2 text-[11px]" data-testid={`mirror-tenant-details-${t.id}`}>
                    {/* Batch #14.218 — Bouton « Modifier » pour ouvrir le modal d'édition */}
                    {onEdit && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onEdit(t); }}
                          className="text-[10px] px-2 py-1 rounded border bg-white border-blue-300 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1"
                          data-testid={`mirror-tenant-edit-${t.id}`}
                        >
                          <Pencil className="w-3 h-3" /> Modifier ce locataire
                        </button>
                      </div>
                    )}
                    <DetailRow label="Nom complet">{fullName}</DetailRow>
                    <DetailRow label="Téléphone">
                      {t.phone ? (
                        <a href={`tel:${t.phone}`} className="text-blue-600 hover:underline inline-flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {t.phone}
                        </a>
                      ) : <span className="italic text-slate-400">Non renseigné</span>}
                    </DetailRow>
                    <DetailRow label="Courriel">
                      {t.email ? (
                        <a href={`mailto:${t.email}`} className="text-blue-600 hover:underline inline-flex items-center gap-1 break-all">
                          <Mail className="w-3 h-3" /> {t.email}
                        </a>
                      ) : <span className="italic text-slate-400">Non renseigné</span>}
                    </DetailRow>
                    {ctx.unit && (
                      <DetailRow label="Logement">
                        Apt {ctx.unit.unit_label || ctx.unit.id.slice(0, 6)}
                        {ctx.building?.name && ` — ${ctx.building.name}`}
                        {ctx.building && (
                          <div className="text-slate-500 mt-0.5">
                            {[ctx.building.address_line, ctx.building.city, ctx.building.province, ctx.building.postal_code].filter(Boolean).join(", ")}
                          </div>
                        )}
                      </DetailRow>
                    )}
                    {t.rent_amount > 0 && <DetailRow label="Loyer (override)">{fmtMoney(t.rent_amount)}/mois</DetailRow>}
                    {ctx.lease?.rent_monthly && !t.rent_amount && (
                      <DetailRow label="Loyer">{fmtMoney(ctx.lease.rent_monthly)}/mois (via bail)</DetailRow>
                    )}
                    {ctx.lease?.start_date && (
                      <DetailRow label="Bail">
                        {ctx.lease.start_date} → {ctx.lease.end_date || "—"}
                        <span className="ml-2 text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          {ctx.lease.status}
                        </span>
                      </DetailRow>
                    )}
                    {t.previous_address && <DetailRow label="Adresse précédente">{t.previous_address}</DetailRow>}
                    {t.emergency_contact_name && (
                      <DetailRow label="Contact urgence">
                        {t.emergency_contact_name}
                        {t.emergency_contact_phone && (
                          <> · <a href={`tel:${t.emergency_contact_phone}`} className="text-blue-600 hover:underline">{t.emergency_contact_phone}</a></>
                        )}
                      </DetailRow>
                    )}
                    {t.notes && <DetailRow label="Notes">{t.notes}</DetailRow>}
                    {t.balance_due != null && t.balance_due !== 0 && (
                      <DetailRow label="Solde">
                        <span className={t.balance_due > 0 ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold"}>
                          {fmtMoney(t.balance_due)}
                        </span>
                      </DetailRow>
                    )}

                    {/* Batch #14.183 + #14.184 — Actions de contact manuel (filet de sécurité) */}
                    {(t.email || t.phone) && (
                      <div className="pt-2 mt-2 border-t border-slate-200">
                        <div className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Contact manuel</div>
                        <ManualEmailActions tenant={t} ctx={ctx} fullName={fullName} />
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export { TenantsPanel };
